// src/theme.ts
/* =========================================================================================================================== */
/* MUIのテーマと、MUI外でも参照できる共通デザイントークンをまとめたファイルです。                                            */
/*                                                                                                                             */
/* 使い分け                                                                                                                    */
/* - MUIコンポーネントの sx / styled 内: theme.palette や "background.paper" などを使用                                       */
/* - グラフ設定、テンプレート文字列、通常のオブジェクト: designTokens を使用                                                   */
/* =========================================================================================================================== */

import { alpha, createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid-pro/themeAugmentation";

/* =========================================================================================================================== */
/* 共通デザイントークン                                                                                                       */
/* =========================================================================================================================== */
/*
 * MUIのテーマ外でも同じ色を使うための共通定数です。
 *
 * 使用例:
 *   import { designTokens } from "../theme";
 *
 *   const style = {
 *     backgroundColor: designTokens.surface.paper,
 *     color: designTokens.text.primary,
 *     border: `1px solid ${designTokens.border.default}`,
 *   };
 */
export const designTokens = {
  /*
   * brand.main / brand.dark / brand.light は、
   * 既存画面との互換性を保つための別名です。
   * 新規実装では primary / primaryDark / primaryLight の利用を推奨します。
   */
  brand: {
    primary: "#98BC2E",
    primaryLight: "#C5E763",
    primaryDark: "#76951F",
    secondary: "#334155",

    // 既存コード互換用
    main: "#98BC2E",
    light: "#C5E763",
    dark: "#76951F",
  },

  /*
   * page / strong は、既存画面で使用されている互換キーです。
   * page は画面全体、strong は少し強調した面に使用します。
   */
  surface: {
    background: "#F4F6F8",
    page: "#F4F6F8",
    paper: "#FFFFFF",
    subtle: "#F8FAFC",
    strong: "#EEF2F6",
    elevated: "#FFFFFF",
    disabled: "#F1F5F9",
  },

  text: {
    primary: "#1E293B",
    secondary: "#64748B",
    muted: "#94A3B8",
    inverse: "#FFFFFF",
  },

  border: {
    default: "#E2E8F0",
    strong: "#CBD5E1",
    focus: "#76951F",
  },

  /*
   * overlay は透明色専用です。
   * styled() やグラフ設定など、MUIの palette 参照が使えない箇所で利用します。
   */
  overlay: {
    brandSoft: "rgba(152, 188, 46, 0.10)",
    brandMedium: "rgba(152, 188, 46, 0.32)",
    brandStrong: "rgba(152, 188, 46, 0.52)",
    whiteSoft: "rgba(255, 255, 255, 0.72)",
    blackSoft: "rgba(15, 23, 42, 0.08)",
  },

  /*
   * status と state は同じ意味です。
   * state は既存コード互換用として残しています。
   */
  status: {
    success: "#42955C",
    warning: "#D98E04",
    error: "#DC5A52",
    info: "#3286B8",
  },

  state: {
    success: "#42955C",
    warning: "#D98E04",
    error: "#DC5A52",
    info: "#3286B8",
  },

  shadow: {
    small: "0 1px 2px rgba(15, 23, 42, 0.05)",
    medium: "0 8px 24px rgba(15, 23, 42, 0.08)",
    large: "0 24px 64px rgba(15, 23, 42, 0.18)",
  },

  radius: {
    small: 8,
    medium: 10,
    large: 14,
    dialog: 16,
  },
} as const;

/* =========================================================================================================================== */
/* MUIテーマ                                                                                                                   */
/* =========================================================================================================================== */

const theme = createTheme({
  palette: {
    mode: "light",

    primary: {
      main: designTokens.brand.primary,
      light: designTokens.brand.primaryLight,
      dark: designTokens.brand.primaryDark,
      contrastText: "#17210B",
    },

    secondary: {
      main: designTokens.brand.secondary,
      light: "#64748B",
      dark: "#1E293B",
      contrastText: designTokens.text.inverse,
    },

    success: {
      main: designTokens.status.success,
      contrastText: designTokens.text.inverse,
    },

    warning: {
      main: designTokens.status.warning,
      contrastText: designTokens.text.inverse,
    },

    error: {
      main: designTokens.status.error,
      contrastText: designTokens.text.inverse,
    },

    info: {
      main: designTokens.status.info,
      contrastText: designTokens.text.inverse,
    },

    text: {
      primary: designTokens.text.primary,
      secondary: designTokens.text.secondary,
      disabled: designTokens.text.muted,
    },

    background: {
      default: designTokens.surface.background,
      paper: designTokens.surface.paper,
    },

    divider: designTokens.border.default,

    action: {
      hover: alpha(designTokens.brand.primary, 0.08),
      selected: alpha(designTokens.brand.primary, 0.14),
      focus: alpha(designTokens.brand.primary, 0.2),
      disabled: designTokens.text.muted,
      disabledBackground: designTokens.surface.disabled,
    },
  },

  typography: {
    fontFamily: [
      '"Noto Sans JP"',
      '"Yu Gothic"',
      '"YuGothic"',
      '"Hiragino Kaku Gothic ProN"',
      "Meiryo",
      "sans-serif",
    ].join(","),

    h1: {
      fontSize: "2rem",
      fontWeight: 700,
      lineHeight: 1.35,
      letterSpacing: "-0.02em",
    },

    h2: {
      fontSize: "1.75rem",
      fontWeight: 700,
      lineHeight: 1.4,
      letterSpacing: "-0.015em",
    },

    h3: {
      fontSize: "1.5rem",
      fontWeight: 700,
      lineHeight: 1.4,
    },

    h4: {
      fontSize: "1.35rem",
      fontWeight: 700,
      lineHeight: 1.45,
    },

    h5: {
      fontSize: "1.15rem",
      fontWeight: 700,
      lineHeight: 1.5,
    },

    h6: {
      fontSize: "1rem",
      fontWeight: 700,
      lineHeight: 1.5,
    },

    body1: {
      fontSize: "0.95rem",
      lineHeight: 1.7,
    },

    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.65,
    },

    button: {
      fontWeight: 700,
      textTransform: "none",
    },

    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.5,
    },
  },

  shape: {
    borderRadius: designTokens.radius.medium,
  },

  components: {
    /* ===================== ブラウザ全体 ===================== */
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: designTokens.surface.background,
          color: designTokens.text.primary,
          minHeight: "100vh",
        },

        "*": {
          boxSizing: "border-box",
        },

        "::selection": {
          backgroundColor: alpha(designTokens.brand.primary, 0.25),
        },

        "*::-webkit-scrollbar": {
          width: 8,
          height: 8,
        },

        "*::-webkit-scrollbar-thumb": {
          backgroundColor: designTokens.border.strong,
          borderRadius: 999,
        },

        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: designTokens.text.muted,
        },
      },
    },

    /* ===================== Paper / Card ===================== */
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },

      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderColor: designTokens.border.default,
        },

        rounded: {
          borderRadius: 12,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${designTokens.border.default}`,
          borderRadius: designTokens.radius.large,
          backgroundColor: designTokens.surface.paper,
          transition:
            "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",

          "&:hover": {
            borderColor: alpha(designTokens.brand.primary, 0.45),
            boxShadow: designTokens.shadow.medium,
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,

          "&:last-child": {
            paddingBottom: 20,
          },
        },
      },
    },

    /* ===================== AppBar ===================== */
    MuiAppBar: {
      defaultProps: {
        color: "inherit",
        elevation: 0,
      },

      styleOverrides: {
        root: {
          backgroundColor: alpha(designTokens.surface.paper, 0.92),
          color: designTokens.text.primary,
          borderBottom: `1px solid ${designTokens.border.default}`,
          backdropFilter: "blur(10px)",
        },
      },
    },

    /* ===================== Button ===================== */
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },

      styleOverrides: {
        root: {
          minHeight: 38,
          borderRadius: 9,
          paddingInline: 16,
          fontWeight: 700,
          textTransform: "none",

          "&:active": {
            transform: "translateY(1px)",
          },
        },

        containedPrimary: {
          color: "#17210B",
          backgroundColor: designTokens.brand.primary,

          "&:hover": {
            color: "#17210B",
            backgroundColor: designTokens.brand.primaryLight,
            boxShadow: `0 5px 14px ${alpha(designTokens.brand.primary, 0.3)}`,
          },

          "&.Mui-disabled": {
            color: designTokens.text.secondary,
            backgroundColor: "#DDE7C2",
          },
        },

        containedSecondary: {
          color: designTokens.text.inverse,
          backgroundColor: designTokens.brand.secondary,

          "&:hover": {
            backgroundColor: "#475569",
          },
        },

        outlinedPrimary: {
          color: designTokens.brand.primaryDark,
          borderColor: alpha(designTokens.brand.primaryDark, 0.45),

          "&:hover": {
            color: designTokens.brand.primaryDark,
            borderColor: designTokens.brand.primaryDark,
            backgroundColor: alpha(designTokens.brand.primary, 0.08),
          },
        },
      },
    },

    /* ===================== IconButton ===================== */
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          color: designTokens.text.secondary,

          "&:hover": {
            color: designTokens.brand.primaryDark,
            backgroundColor: alpha(designTokens.brand.primary, 0.1),
          },
        },
      },
    },

    /* ===================== TextField / Input ===================== */
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 9,
          backgroundColor: designTokens.surface.paper,
          color: designTokens.text.primary,

          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: designTokens.text.muted,
          },

          "&.Mui-focused": {
            backgroundColor: designTokens.surface.paper,
            boxShadow: `0 0 0 3px ${alpha(designTokens.brand.primary, 0.18)}`,

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: designTokens.brand.primaryDark,
              borderWidth: 1,
            },
          },

          "&.Mui-error": {
            boxShadow: `0 0 0 3px ${alpha(designTokens.status.error, 0.1)}`,

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: designTokens.status.error,
            },
          },

          "&.Mui-disabled": {
            backgroundColor: designTokens.surface.disabled,
          },

          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: designTokens.border.default,
          },
        },

        notchedOutline: {
          borderColor: designTokens.border.strong,
        },

        input: {
          paddingBlock: 10,
          fontSize: "0.9rem",

          "&::placeholder": {
            color: designTokens.text.muted,
            opacity: 1,
          },

          "&.Mui-disabled": {
            opacity: 1,
            WebkitTextFillColor: designTokens.text.muted,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: designTokens.text.secondary,

          "&.Mui-focused": {
            color: designTokens.brand.primaryDark,
          },

          "&.Mui-error": {
            color: designTokens.status.error,
          },
        },

        asterisk: {
          color: designTokens.status.error,
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 2,
          color: designTokens.text.secondary,

          "&.Mui-error": {
            color: designTokens.status.error,
          },
        },
      },
    },

    /* ===================== Select / Menu ===================== */
    MuiSelect: {
      defaultProps: {
        size: "small",
      },

      styleOverrides: {
        select: {
          display: "flex",
          alignItems: "center",
        },

        icon: {
          color: designTokens.text.secondary,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 6,
          border: `1px solid ${designTokens.border.default}`,
          borderRadius: 10,
          boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 7,
          marginInline: 6,
          marginBlock: 2,

          "&.Mui-selected": {
            color: designTokens.brand.primaryDark,
            backgroundColor: alpha(designTokens.brand.primary, 0.14),

            "&:hover": {
              backgroundColor: alpha(designTokens.brand.primary, 0.2),
            },
          },
        },
      },
    },

    /* ===================== Dialog ===================== */
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: designTokens.radius.dialog,
          backgroundColor: designTokens.surface.paper,
          color: designTokens.text.primary,
          boxShadow: designTokens.shadow.large,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          position: "sticky",
          top: 0,
          zIndex: 1,
          padding: "18px 24px",
          backgroundColor: designTokens.surface.paper,
          color: designTokens.text.primary,
          borderBottom: `1px solid ${designTokens.border.default}`,
          fontSize: "1.1rem",
          fontWeight: 700,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 24,
        },

        dividers: {
          borderColor: designTokens.border.default,
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px",
          borderTop: `1px solid ${designTokens.border.default}`,
        },
      },
    },

    /* ===================== Tabs ===================== */
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },

        indicator: {
          height: 3,
          borderRadius: "3px 3px 0 0",
          backgroundColor: designTokens.brand.primaryDark,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          minWidth: 96,
          color: designTokens.text.secondary,
          fontWeight: 700,
          textTransform: "none",

          "&.Mui-selected": {
            color: designTokens.brand.primaryDark,
          },
        },
      },
    },

    /* ===================== Chip ===================== */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          fontWeight: 700,
        },

        filledPrimary: {
          color: designTokens.brand.primaryDark,
          backgroundColor: alpha(designTokens.brand.primary, 0.18),
        },

        outlinedPrimary: {
          color: designTokens.brand.primaryDark,
          borderColor: alpha(designTokens.brand.primaryDark, 0.4),
        },
      },
    },

    /* ===================== Table / DataGrid ===================== */
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: designTokens.border.default,
        },

        head: {
          color: designTokens.text.secondary,
          backgroundColor: designTokens.surface.subtle,
          fontWeight: 700,
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: alpha(designTokens.brand.primary, 0.05),
          },
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${designTokens.border.default}`,
          borderRadius: 12,
          backgroundColor: designTokens.surface.paper,
          color: designTokens.text.primary,

          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
            outline: "none",
          },
        },

        columnHeaders: {
          backgroundColor: designTokens.surface.subtle,
          color: designTokens.text.secondary,
          borderBottom: `1px solid ${designTokens.border.default}`,
          fontWeight: 700,
        },

        row: {
          "&:hover": {
            backgroundColor: alpha(designTokens.brand.primary, 0.05),
          },

          "&.Mui-selected": {
            backgroundColor: alpha(designTokens.brand.primary, 0.1),

            "&:hover": {
              backgroundColor: alpha(designTokens.brand.primary, 0.15),
            },
          },
        },

        cell: {
          borderBottom: `1px solid ${designTokens.border.default}`,
        },

        footerContainer: {
          borderTop: `1px solid ${designTokens.border.default}`,
          backgroundColor: designTokens.surface.subtle,
        },
      },
    },

    /* ===================== Alert ===================== */
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          alignItems: "center",
        },

        standardSuccess: {
          backgroundColor: alpha(designTokens.status.success, 0.1),
          color: "#2E6E42",
        },

        standardWarning: {
          backgroundColor: alpha(designTokens.status.warning, 0.12),
          color: "#8A5900",
        },

        standardError: {
          backgroundColor: alpha(designTokens.status.error, 0.1),
          color: "#9C3B35",
        },

        standardInfo: {
          backgroundColor: alpha(designTokens.status.info, 0.1),
          color: "#205F84",
        },
      },
    },

    /* ===================== Tooltip ===================== */
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 7,
          padding: "7px 10px",
          fontSize: "0.75rem",
          backgroundColor: designTokens.text.primary,
        },

        arrow: {
          color: designTokens.text.primary,
        },
      },
    },

    /* ===================== Checkbox / Radio / Switch ===================== */
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: designTokens.text.muted,

          "&.Mui-checked": {
            color: designTokens.brand.primaryDark,
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: designTokens.text.muted,

          "&.Mui-checked": {
            color: designTokens.brand.primaryDark,
          },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: designTokens.brand.primaryDark,

            "& + .MuiSwitch-track": {
              backgroundColor: designTokens.brand.primary,
              opacity: 1,
            },
          },
        },

        track: {
          backgroundColor: designTokens.border.strong,
          opacity: 1,
        },
      },
    },

    /* ===================== Divider / Progress / Avatar ===================== */
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: designTokens.border.default,
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 7,
          borderRadius: 999,
          backgroundColor: "#E8EFD5",
        },

        bar: {
          borderRadius: 999,
          backgroundColor: designTokens.brand.primaryDark,
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          color: designTokens.brand.primaryDark,
          backgroundColor: alpha(designTokens.brand.primary, 0.18),
          fontWeight: 700,
        },
      },
    },
  },
});

/*
 * default export:
 *   import theme from "../theme";
 *
 * named export:
 *   import { designTokens } from "../theme";
 */
export default theme;
